import { Level, Location, Site } from '@/core/entities';
import { Stack, capitalize, IconButton, Paper, Box } from '@mui/material';
import { EditRounded, DeleteRounded } from '@mui/icons-material';
import { FunctionComponent, ReactElement, useMemo } from 'react';
import useAuth from '@/shared/context/auth-context';
import { Roles } from '@/core/types';
import LocationsTableHeader from '../table/LocationsTableHeader';
import Backdrop from '@/components/ui/backdrop';
import DataGrid, { Column, FilterRow, HeaderFilter, Pager, Paging, SearchPanel, Sorting, Toolbar, Item } from 'devextreme-react/data-grid';

export interface LocationsManagementBaseProps {
	site: Site;
	level: Level;
	parentLocationId?: string;
	header: {
		controlLocationsTemplate?: boolean;
	};
	table: {
		data: Location[];
		isLoading?: boolean;
		rowAction?: (location: Location) => void;
		columnActions?: {
			edit?: (location: Location) => void;
			remove?: (location: Location) => void;
		};
	};
	invalidateData?: () => Promise<void> | void;
}

const LocationManagementBase: FunctionComponent<LocationsManagementBaseProps> = ({
	site,
	level,
	parentLocationId,
	header,
	table,
	invalidateData,
}): ReactElement => {
	const { user } = useAuth();
	const { controlLocationsTemplate } = header;
	const { data, isLoading, rowAction, columnActions } = table;

	const { role } = user || {};
	const hasActionsColumn = useMemo(
		() => role && ![Roles.OPERATOR, Roles.SITE_SUPERVISOR].includes(role),
		[role]
	);
	const canEditLocation = useMemo(
		() => role && [Roles.LEVIANT_ADMIN, Roles.ACCOUNT_ADMIN, Roles.SITE_ADMIN].includes(role),
		[role]
	);
	const canRemoveLocation = useMemo(() => role && [Roles.LEVIANT_ADMIN].includes(role), [role]);

	const { edit, remove } = columnActions || {};

	const handleRowClick = (e: { data: Location }) => {
		if (rowAction) {
			rowAction(e.data);
		}
	};

	const renderActionsCell = (cellData: { data: Location }) => {
		const location = cellData.data;
		return (
			<Stack direction="row" spacing={1}>
				{canEditLocation && (
					<IconButton
						size="small"
						color="primary"
						onClick={(e) => {
							e.stopPropagation();
							edit?.(location);
						}}
					>
						<EditRounded fontSize="small" />
					</IconButton>
				)}
				{canRemoveLocation && (
					<IconButton
						size="small"
						color="error"
						onClick={(e) => {
							e.stopPropagation();
							remove?.(location);
						}}
					>
						<DeleteRounded fontSize="small" />
					</IconButton>
				)}
			</Stack>
		);
	};

	return (
		<Stack direction="column" spacing={2.5}>
			{isLoading ? (
				<Backdrop />
			) : (
				<Box component={Paper} p={3}>
					<DataGrid
						dataSource={data}
						showBorders={true}
						columnAutoWidth={true}
						rowAlternationEnabled={true}
						allowColumnResizing={true}
						allowColumnReordering={true}
						onRowClick={rowAction ? handleRowClick : undefined}
					>
						<Sorting mode="multiple" />
						<FilterRow visible={true} />
						<HeaderFilter visible={true} />
						<SearchPanel visible={true} highlightCaseSensitive={true} />
						<Paging defaultPageSize={50} enabled={true} />
						<Pager
							visible={true}
							showPageSizeSelector={true}
							showInfo={true}
							allowedPageSizes={[25, 50, 100]}
						/>
						<Toolbar>
							<Item location="before">
								<LocationsTableHeader
									site={site}
									level={level}
									parentLocationId={parentLocationId}
									refetchLocations={invalidateData}
									controlLocationTemplate={controlLocationsTemplate}
								/>
							</Item>
							<Item name="searchPanel" location="after" />
						</Toolbar>

						<Column dataField="name" caption={capitalize(level.name)} />
						{hasActionsColumn && (
							<Column
								caption="Actions"
								width={100}
								allowFiltering={false}
								allowSorting={false}
								allowReordering={false}
								cellRender={renderActionsCell}
							/>
						)}
					</DataGrid>
				</Box>
			)}
		</Stack>
	);
};

export default LocationManagementBase;
